import { Order, OrderItem, OrderItems } from '@spike/order-model';
import uniq from 'lodash/uniq';
import { Moment } from 'moment-timezone';

export const sortPets = (order: Order) => {
    const petsWithAppointment = uniq(
        (order?.items.appointments ?? [])
            .sort((item, otherItem) => (item.appointment.id ?? 0) - (otherItem.appointment.id ?? 0))
            .map(item => item.pet)
            .filter(pet => pet !== undefined)
    );

    const petsWithoutAppointment = uniq(
        [
            ...order?.items.addOnServices.map(orderItem => orderItem.pet),
            ...order?.items.products.map(orderItem => orderItem.pet),
            ...order?.items.customs.map(orderItem => orderItem.pet)
        ]
            .filter(pet => pet !== undefined)
            .filter(pet => !petsWithAppointment.some(petWithAppointment => petWithAppointment.id === pet.id))
    );

    return [...petsWithAppointment, ...petsWithoutAppointment];
};

export const groupOrderItemsByPet = (order: Order) => {
    const sortedPets = sortPets(order);

    return sortedPets.reduce((map, pet) => {
        const petOrderItems: OrderItems = {
            appointments: (order?.items.appointments ?? [])
                .filter(item => item.pet.id === pet.id)
                .sort((item, otherItem) =>
                    item.appointment.duration.from.clone().diff(otherItem.appointment.duration.from.clone())
                ),
            products: (order?.items.products ?? [])
                .filter(item => item.orderItem.petId === pet.id)
                .sort((item, otherItem) => (item.orderItem.id ?? 0) - (otherItem.orderItem.id ?? 0)),
            addOnServices: (order?.items.addOnServices ?? [])
                .filter(item => item.orderItem.petId === pet.id)
                .sort((item, otherItem) => (item.orderItem.id ?? 0) - (otherItem.orderItem.id ?? 0)),
            customs: (order?.items.customs ?? [])
                .filter(item => item.orderItem.petId === pet.id)
                .sort((item, otherItem) => (item.orderItem.id ?? 0) - (otherItem.orderItem.id ?? 0)),
            memberships: []
        };

        map.set(pet.id ?? 0, petOrderItems);

        return map;
    }, new Map<number, OrderItems>());
};

export const getOrderStartTime = (order: Order) =>
    order.items.appointments.reduce((earlier: Moment | null, appointmentOrder) => {
        const appointmentStart = appointmentOrder.appointment.duration.from;

        if (earlier === null || appointmentStart.isBefore(earlier)) {
            return appointmentStart.clone();
        } else {
            return earlier;
        }
    }, null);

export const getOrderEndTime = (order: Order) =>
    order.items.appointments.reduce((latest: Moment | null, appointmentOrder) => {
        const appointmentEnd = appointmentOrder.appointment.duration.to;

        if (latest === null || appointmentEnd.isAfter(latest)) {
            return appointmentEnd.clone();
        } else {
            return latest;
        }
    }, null);

export const formatAmount = (amount: string | null | undefined): string => {
    if (amount === undefined || amount === null) {
        return '';
    } else if (amount.startsWith('-')) {
        return `-$${amount.substring(1)}`;
    } else {
        return `$${amount}`;
    }
};

export const getAllOrderItems = (order: Order) => getAllOrderItemsByItems(order.items);

export const getAllOrderItemsByItems = (orderItems: OrderItems) => [
    ...orderItems.appointments.map(item => item.orderItem),
    ...orderItems.addOnServices.map(item => item.orderItem),
    ...orderItems.products.map(item => item.orderItem),
    ...orderItems.customs.map(item => item.orderItem),
    ...orderItems.memberships.map(item => item.orderItem)
];
